import useCurrentTime from 'hooks/useCurrentTime';
import { useCallback, useEffect, useRef } from 'react';

const useComponentTracking = () => {
  const trackingStartTimeReference = useRef<number>();
  const visibleTimeReference = useRef(0);
  const lastVisibleStartTimeReference = useRef<number>();
  const isTracking = useRef<boolean>(false);
  const { getCurrentTime } = useCurrentTime();

  const updateVisibleTime = useCallback(() => {
    if (!isTracking.current) return;
    const currentTime = getCurrentTime();
    if (document.visibilityState === 'hidden') {
      if (lastVisibleStartTimeReference.current) {
        visibleTimeReference.current += currentTime - lastVisibleStartTimeReference.current;
        lastVisibleStartTimeReference.current = undefined;
      }
    } else if (!lastVisibleStartTimeReference.current) {
      lastVisibleStartTimeReference.current = currentTime;
    }
  }, [getCurrentTime]);

  useEffect(() => {
    document.addEventListener('visibilitychange', updateVisibleTime);

    return () => {
      const currentTime = getCurrentTime();
      if (lastVisibleStartTimeReference.current && isTracking.current) {
        visibleTimeReference.current += currentTime - lastVisibleStartTimeReference.current;
      }

      document.removeEventListener('visibilitychange', updateVisibleTime);
    };
  }, [getCurrentTime, updateVisibleTime]);

  const getVisibleDuration = useCallback(() => {
    const currentTime = getCurrentTime();
    const visibleDuration =
      visibleTimeReference.current +
      (document.visibilityState === 'visible' && lastVisibleStartTimeReference.current
        ? currentTime - lastVisibleStartTimeReference.current
        : 0);
    return visibleDuration;
  }, [getCurrentTime]);

  const getVisibleDurationAndCurrentTime = useCallback(() => {
    const currentTime = getCurrentTime();
    const visibleDuration =
      visibleTimeReference.current +
      (document.visibilityState === 'visible' && lastVisibleStartTimeReference.current
        ? currentTime - lastVisibleStartTimeReference.current
        : 0);
    return { visibleDuration, currentTime };
  }, [getCurrentTime]);

  const getTrackingDuration = useCallback(() => {
    if (!trackingStartTimeReference.current) return 0;
    return getCurrentTime() - trackingStartTimeReference.current;
  }, [getCurrentTime]);

  const getTrackingStartTime = () => trackingStartTimeReference.current;
  const getLastVisibleTime = () => lastVisibleStartTimeReference.current;
  const getAccumulatedVisibleTime = () => visibleTimeReference.current;

  const startTracking = useCallback(() => {
    isTracking.current = true;
    trackingStartTimeReference.current = getCurrentTime();
    lastVisibleStartTimeReference.current = getCurrentTime();
  }, [getCurrentTime]);

  const stopTracking = useCallback(() => {
    const currentTime = getCurrentTime();
    visibleTimeReference.current +=
      document.visibilityState === 'visible' && lastVisibleStartTimeReference.current
        ? currentTime - (lastVisibleStartTimeReference.current || 0)
        : 0;
    trackingStartTimeReference.current = undefined;
    lastVisibleStartTimeReference.current = undefined;
    isTracking.current = false;
  }, [getCurrentTime]);

  const resetTracking = useCallback(() => {
    isTracking.current = false;
    visibleTimeReference.current = 0;
    trackingStartTimeReference.current = undefined;
    lastVisibleStartTimeReference.current = undefined;
  }, []);

  return {
    getVisibleDuration,
    getTrackingDuration,
    getTrackingStartTime,
    getLastVisibleTime,
    getAccumulatedVisibleTime,
    startTracking,
    stopTracking,
    resetTracking,
    getVisibleDurationAndCurrentTime,
  };
};

export default useComponentTracking;
